import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { css } from 'styled-components'
import { match } from 'ts-pattern'

import { AmountInputBase } from '@root/shared/ui-kit/inputs/amount-input-base'

import { AmountMaxButton } from './amount-max'
import { AmountUsd } from './amount-usd'

import type { CurrencyModel } from '../../model'
import type { AsyncStateN } from '@blade/std-lib'
import type { Error } from '@root/shared'
import type { ClassNameProps } from '@root/shared/react'
import type { BigNumber } from 'bignumber.js'

type ValidationError = 'insufficient-balance'

/**
 * An extra gap for the input and the max button, prevents the value from being cut off in certain cases.
 */
const EXTRA_GAP = 10

type Props = {
  disabled?: boolean
  amountRaw: string
  amountUsdState: AsyncStateN.AsyncStateN<Error.UnknownError, BigNumber>
  validation: ValidationError | undefined
  currency: CurrencyModel
  isMax: boolean
  onMaxClick: () => void
  onChange?: (value: string) => void
} & ClassNameProps

export const CurrencyCenteredAmount: React.FC<Props> = ({
  disabled,
  amountRaw,
  amountUsdState,
  className,
  validation,
  currency,
  onMaxClick,
  isMax,
  onChange,
}) => {
  const ref = useRef<HTMLDivElement>(null)

  const { t } = useTranslation()
  const [size, setSize] = useState(0)

  useEffect(() => {
    const observeTarget = ref.current

    if (!observeTarget) {
      return
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect

        setSize(width)
      }
    })

    resizeObserver.observe(observeTarget)

    return () => {
      resizeObserver.unobserve(observeTarget)
    }
  }, [])

  const renderValidationError = () => {
    return match(validation)
      .with(undefined, () => undefined)
      .with('insufficient-balance', () => (
        <ErrorMessage>
          {t('transfer.validation.insufficient-funds', {
            ticker: currency.ticker,
          })}
        </ErrorMessage>
      ))
      .exhaustive()
  }

  const hasValue = !!amountRaw && amountRaw !== '0'

  return (
    <Root className={className}>
      <AmountContainerHidden $hasValue={hasValue}>
        <AmountFake ref={ref}>{amountRaw}</AmountFake>
        <Side>
          <Ticker>{currency.ticker}</Ticker>
          <MaxButton isMax={isMax} onClick={onMaxClick} />
        </Side>
      </AmountContainerHidden>
      <AmountContainer $hasValue={hasValue}>
        <InputWrapper>
          <AmountInput
            placeholder="0.00"
            // Remove leading zeros from the input value
            onInput={(e: React.FormEvent<HTMLInputElement>) => {
              e.currentTarget.value = e.currentTarget.value.replace(
                /^0+(?=\d)/,
                '',
              )
            }}
            disabled={disabled}
            value={amountRaw}
            onChange={(e) => onChange?.(e.target.value)}
            $width={size}
          />
        </InputWrapper>
        <Side>
          <Ticker>{currency.ticker}</Ticker>
          <MaxButton isMax={isMax} onClick={onMaxClick} />
        </Side>
      </AmountContainer>
      <AmountUsd
        usdAmountState={amountUsdState}
        css={css`
          /* padding-left: ${EXTRA_GAP}px; */
        `}
      />
      {renderValidationError()}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  justify-content: center;
  /* overflow: hidden; */

  /* padding-inline: 10px; */
`

const AmountContainer = styled.div<{ $hasValue: boolean }>`
  color: ${({ $hasValue }) => ($hasValue ? '#fff' : 'var(--text-9)')};

  display: grid;
  grid-template-columns: auto auto;
  justify-content: center;
  gap: 6px;
  width: 100%;

  font-size: 30px;
  font-weight: 700;
  line-height: normal;
`
const InputWrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
`
const AmountContainerHidden = styled(AmountContainer)`
  height: 0;
  overflow: hidden;
`

const MaxButton = styled(AmountMaxButton)`
  margin-top: 2px;
`

const AmountInput = styled(AmountInputBase)<{
  $width: number
}>`
  width: ${({ $width }) => $width}px;

  min-width: 70px;

  color: var(--text-12);
  background: transparent;
  border: none;
  outline: none;

  font-size: 30px;
  font-weight: 700;
  line-height: normal;

  text-align: right;
`
const AmountFake = styled.div`
  color: var(--text-12);
  background: #222;
  border: none;
  outline: none;

  font-size: 30px;
  font-weight: 700;
  line-height: normal;

  text-align: right;
`

const Ticker = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;

  display: grid;
  align-items: end;
`

const Side = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 8px;
  justify-items: start;
  justify-content: start;

  /* padding-inline-end: ${EXTRA_GAP}px; */
`

const ErrorMessage = styled.div`
  color: var(--error-10);

  text-align: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;

  margin-top: 8px;
`
